/**
 * Settings API Functions
 * Handles API calls for system settings management.
 */

import { apiClient } from './client'
import type { ApiResponse } from './types'
import type {
  SystemSetting,
  SystemSettingsResponse,
  SystemSettingsBulkUpdate,
  PasswordPolicy
} from '@/types/settings'

/** Public endpoint: get current password policy (no auth required). Used for registration/change-password forms. */
export const getPasswordPolicy = async (): Promise<ApiResponse<PasswordPolicy>> => {
  try {
    const response = await apiClient.get<PasswordPolicy>('/users/settings/password-policy')
    // apiClient's interceptor already returns the backend envelope, so
    // `response.data` IS the PasswordPolicy payload — reading `.data.data`
    // was one level too deep and silently resolved to undefined (#585/#623).
    return {
      success: true,
      data: response.data ?? undefined
    }
  } catch (error) {
    return {
      success: false,
      message: 'Failed to load password policy'
    }
  }
}

// A failed load reports success: false. It used to pretend success with an
// empty list, which the System Settings page hydrated as "maintenance off" —
// a following Save then turned maintenance mode off for real.
export const getSettings = async (): Promise<ApiResponse<SystemSettingsResponse>> => {
  try {
    return await apiClient.get('/users/admin/settings')
  } catch (error) {
    return {
      success: false,
      message: 'Failed to load system settings'
    }
  }
}

export const getSetting = async (key: string): Promise<ApiResponse<SystemSetting>> => {
  try {
    return await apiClient.get(`/users/admin/settings/${key}`)
  } catch (error) {
    return {
      success: false,
      message: 'Setting not found'
    }
  }
}

// The service_config value column is JSONB — backend consumers read
// booleans (MAINTENANCE_MODE, REGISTRATION_ENABLED), numbers
// (SESSION_TIMEOUT) and objects (password_policy), not just strings.
// `null` is meaningful: an explicit reset to the env/code default.
export const updateSetting = async (
  key: string,
  value: string | number | boolean | object | null
): Promise<ApiResponse<SystemSetting>> => {
  return apiClient.put(`/users/admin/settings/${key}`, { value })
}

export const updateSettings = async (
  updates: SystemSettingsBulkUpdate
): Promise<ApiResponse<SystemSettingsResponse>> => {
  // PUT /users/admin/settings expects { updates: [{key, value}] } —
  // sending the { settings: [...] } wrapper as-is 400s.
  return apiClient.put('/users/admin/settings', { updates: updates.settings })
}

